import React from 'react';
import { ContentState, EditorState, RichUtils, type EditorState as EditorStateType } from 'draft-js';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { Grid, ToggleButtonGroup } from '@mui/material';

import useSettings from 'app/main/components/hooks/useSettings';
import useSpeechToText from 'app/main/components/hooks/useSpeechToText';
import useMountEffect from 'app/main/components/hooks/useMountEffect';
import decoratorList from '../ComponentPlugins';
import DialogAddLink from '../dialogs/DialogAddLink';
import DialogHeaderStyle from '../dialogs/DialogHeaderStyle';
import EditorHtmlToolbarButton from './EditorHtmlToolbarButton';

interface EditorHtmlToolbarProps {
	/** Draft.js editor state */
	editorState: EditorStateType;
	/** Setter for the editor state */
	setEditorState: React.Dispatch<React.SetStateAction<EditorStateType>>;
}

const getSelectionEditorState = state => {
	const currentSelection = state.getSelection();
	const stateWithContentAndSelection = EditorState.forceSelection(state, currentSelection);

	return stateWithContentAndSelection;
};

/** Html editor toolbar */
export default function EditorHtmlToolbar({ editorState, setEditorState }: EditorHtmlToolbarProps) {
	const { t } = useSettings();
	const { isSpeechToTextEnabled } = useSpeechToText();

	// TEXT STYLE
	const [currentStyle, setCurrentStyle] = React.useState<string[]>([]);

	const changeInlineStyle = (_event, style) => {
		const styleState = RichUtils.toggleInlineStyle(editorState, style);
		setEditorState(getSelectionEditorState(styleState));
	};

	const changeBlockType = (_event, type) => {
		const blockState = RichUtils.toggleBlockType(editorState, type);
		setEditorState(getSelectionEditorState(blockState));
	};

	// HEADER DIALOG
	const [headerStyle, setHeaderStyle] = React.useState('paragraph');
	const [headerDialogAnchor, setHeaderDialogAnchor] = React.useState<HTMLButtonElement | null>(null);
	const [openHeaderDialog, setOpenHeaderDialog] = React.useState(false);
	const switchOpenHeaderDialog = () => setOpenHeaderDialog(!openHeaderDialog);

	// LINK DIALOG
	const [linkDialogAnchor, setLinkDialogAnchor] = React.useState<HTMLButtonElement | null>(null);
	const [openLinkDialog, setOpenLinkDialog] = React.useState(false);
	const switchOpenLinkDialog = () => setOpenLinkDialog(!openLinkDialog);

	const editorStateSelection = editorState.getSelection();
	const isLinkButtonDisabled = editorStateSelection.getEndOffset() - editorStateSelection.getStartOffset() <= 0;

	// SPEECH-TO-TEXT
	const [sttEnabled, setSttEnabled] = React.useState(false);
	const [listening, setListening] = React.useState(false);
	const { transcript } = useSpeechRecognition({ clearTranscriptOnListen: false });

	const toggleStt = () => {
		if (!listening) {
			// resetTranscript();
			// check if browser support stt
			if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
				// handle not support stuff
				return;
			}
			setListening(true);
			SpeechRecognition.startListening({ language: 'it-IT', continuous: true });
		} else {
			setListening(false);
			SpeechRecognition.stopListening();
			setEditorState(EditorState.createWithContent(ContentState.createFromText(transcript), decoratorList));
		}
	};

	// UNDO/REDO
	const undoStack = editorState.getUndoStack();
	const redoStack = editorState.getRedoStack();

	const handleUndo = () => {
		setEditorState(EditorState.undo(editorState));
	};

	const handleRedo = () => {
		setEditorState(EditorState.redo(editorState));
	};

	// Set current style based on the selected block
	React.useEffect(() => {
		setCurrentStyle(editorState.getCurrentInlineStyle().toArray());
	}, [editorState]);

	// Get if Speech-To-Text is enabled
	useMountEffect(() => {
		isSpeechToTextEnabled().then(isEnabled => setSttEnabled(isEnabled));
	});

	return (
		<Grid container spacing={1} className="mb-8">
			<Grid>
				<ToggleButtonGroup size="small" value={currentStyle}>
					<EditorHtmlToolbarButton
						value="BOLD"
						icon="format-bold"
						tooltip={t('HTMLEDITOR_BOLD')}
						onClick={changeInlineStyle}
					/>
					<EditorHtmlToolbarButton
						value="ITALIC"
						icon="format-italic"
						tooltip={t('HTMLEDITOR_ITALIC')}
						onClick={changeInlineStyle}
					/>
					<EditorHtmlToolbarButton
						value="UNDERLINE"
						icon="format-underline"
						tooltip={t('HTMLEDITOR_UNDERLINE')}
						onClick={changeInlineStyle}
					/>
					<EditorHtmlToolbarButton
						value="STRIKETHROUGH"
						icon="format-strikethrough"
						tooltip={t('HTMLEDITOR_STRIKETHROUGH')}
						onClick={changeInlineStyle}
					/>
				</ToggleButtonGroup>
			</Grid>
			<Grid>
				<ToggleButtonGroup size="small">
					<EditorHtmlToolbarButton
						value={headerStyle}
						icon={
							headerStyle === 'header-one'
								? 'format-header-1'
								: headerStyle === 'header-two'
									? 'format-header-2'
									: headerStyle === 'header-three'
										? 'format-header-3'
										: 'format-paragraph'
						}
						secondaryIcon="menu-down"
						tooltip={
							headerStyle === 'header-one'
								? t('HTMLEDITOR_H1')
								: headerStyle === 'header-two'
									? t('HTMLEDITOR_H2')
									: headerStyle === 'header-three'
										? t('HTMLEDITOR_H3')
										: t('HTMLEDITOR_P')
						}
						onPointerEnter={e => setHeaderDialogAnchor(e.currentTarget)}
						onClick={switchOpenHeaderDialog}
					/>
					<DialogHeaderStyle
						anchorElement={headerDialogAnchor}
						open={openHeaderDialog}
						handleClose={switchOpenHeaderDialog}
						selectedHeaderStyle={headerStyle}
						handleChange={value => {
							setHeaderStyle(value);
							changeBlockType(null, value);
						}}
					/>
				</ToggleButtonGroup>
			</Grid>
			<Grid>
				<ToggleButtonGroup size="small" value={currentStyle} exclusive onChange={changeBlockType}>
					<EditorHtmlToolbarButton
						value="ordered-list-item"
						icon="format-list-numbered"
						tooltip={t('HTMLEDITOR_NUMBEREDLIST')}
					/>
					<EditorHtmlToolbarButton
						value="unordered-list-item"
						icon="format-list-bulleted"
						tooltip={t('HTMLEDITOR_BULLETEDLIST')}
					/>
				</ToggleButtonGroup>
			</Grid>
			<Grid>
				<ToggleButtonGroup size="small">
					<EditorHtmlToolbarButton
						value="LINK"
						icon="link-variant"
						secondaryIcon="add"
						tooltip={t('HTMLEDITOR_ADDLINK')}
						onPointerEnter={e => setLinkDialogAnchor(e.currentTarget)}
						onClick={switchOpenLinkDialog}
						disabled={isLinkButtonDisabled}
					/>
					<DialogAddLink
						anchorElement={linkDialogAnchor}
						open={openLinkDialog}
						handleClose={switchOpenLinkDialog}
						editorState={editorState}
						setEditorState={setEditorState}
						selectedText={window.getSelection()?.toString()}
					/>
				</ToggleButtonGroup>
			</Grid>
			{sttEnabled && (
				<Grid>
					<ToggleButtonGroup size="small">
						<EditorHtmlToolbarButton
							value="STT"
							icon={listening ? 'stop-circle-outline' : 'microphone'}
							iconColor={listening ? 'red' : undefined}
							tooltip={listening ? t('HTMLEDITOR_STOP_STT') : t('HTMLEDITOR_START_STT')}
							onClick={toggleStt}
						/>
					</ToggleButtonGroup>
				</Grid>
			)}
			<Grid>
				<ToggleButtonGroup size="small">
					<EditorHtmlToolbarButton
						value="UNDO"
						icon="undo"
						tooltip={t('HTMLEDITOR_UNDO')}
						onClick={handleUndo}
						disabled={undoStack.count() === 0}
					/>
					<EditorHtmlToolbarButton
						value="REDO"
						icon="redo"
						tooltip={t('HTMLEDITOR_REDO')}
						onClick={handleRedo}
						disabled={redoStack.count() === 0}
					/>
				</ToggleButtonGroup>
			</Grid>
		</Grid>
	);
}
